// src/pages/bank/BankList.tsx

import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, Banknote } from "lucide-react"; 
import { BankDialog } from "./BankDialog"; 
import { toast } from "react-toastify";
 
import { Bank, BankPayload } from "../../../types/bank";  
import { useBankStore } from "../../../store/bankStore";  
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 


export const BankList = () => {
    const store = useBankStore();
    const { createItem, updateItem, deleteItem } = store; 
    const { handleApiError } = useApiErrorHandler();
    
    // --- STATE'LER ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [bankToEdit, setBankToEdit] = useState<Bank | null>(null);

    // --- ACTION HANDLERS ---
    const handleNew = () => {
        setBankToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (bank: Bank) => {
        setBankToEdit(bank);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (bank: Bank) => {
        const ok = await confirm(`${bank.name} adlı banka hesabını silmek istiyor musun?`);

        if (ok) {
            try {
                await deleteItem(bank.id);
                toast.success("Banka hesabı silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setBankToEdit(null);
    };

    // BankPayload tipine uygun veriyi alır
    const handleSaveBank = async (bankData: BankPayload) => {
        const cleanedData: any = { ...bankData };
 
        try {
            if (bankToEdit) {
                await updateItem(bankToEdit.id, cleanedData);
                toast.success(`Banka Hesabı Güncellendi: ${cleanedData.name}`);
            } else {
                await createItem(cleanedData);
                toast.success(`Yeni Banka Hesabı Eklendi: ${cleanedData.name}`);
            }
            
        } catch (error) {
            handleApiError(error); 
        }
        handleCloseModal(); 
    };

    // --- DataTable body templates ---
    
    // Bakiye formatlama
    const balanceBodyTemplate = (bank: Bank) => (
        <span className={`font-semibold ${bank.balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
             {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: bank.currency || 'TRY' }).format(bank.balance)}
        </span>
    );
    
    // IBAN kısaltma veya gizleme (örnek)
    const ibanBodyTemplate = (bank: Bank) => (
        <span className="font-mono  ">
             {bank.iban ? `**** **** **** ${bank.iban.slice(-4)}` : "-"}
        </span>
    );

    const actionBodyTemplate = (bank: Bank) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(bank)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(bank)
                }
            ]}
        />
    );

    // Banka sütun tanımları
    const bankColumns: DataTableColumn<Bank>[] = [
        { field: "id", header: "ID", width: "80px" },
        { 
            field: "name", 
            header: "Banka Adı", 
            width: "250px", 
            sortable: true, 
        },
        { 
            field: "iban", 
            header: "IBAN", 
            width: "250px", 
            body: ibanBodyTemplate,
            sortable: false,
        },
        { 
            field: "currency", 
            header: "Para Birimi", 
            width: "150px", 
            sortable: true,
        },
        { 
            field: "balance", 
            header: "Mevcut Bakiye", 
            width: "180px", 
            align: "right",
            body: balanceBodyTemplate,
            sortable: true,
        },
        { 
            field: "actions", 
            header: "İşlemler", 
            width: "120px", 
            align: "center",
            body: actionBodyTemplate 
        },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            <EDataTable<Bank> 
            tableKey="bank_list"
                useStore={() => store} 
                dataKey="id"
                title="Banka Hesapları"
                placeholder="Banka Adı Ara"
                searchFields={[
                    { field: "name", operator: "contains" as SimpleFilter["operator"] },
                    { field: "iban", operator: "contains" as SimpleFilter["operator"] },
                ]}
                actions={
                    <TextButton onClick={handleNew} className="btn-new">
                         Yeni Banka Hesabı Ekle
                    </TextButton>
                }
                columns={bankColumns}
            />

            <BankDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                bank={bankToEdit}
                onSave={handleSaveBank}
            />
        </div> 
    );
};